// ============================================================
// CLUBHUB – Zeit im Mitgliederbereich und in den Eingaengen
// ============================================================
// „Sa 24.10.“, „10:30–12:00“, „morgen“, „vor 2 Tagen“. Ohne
// Zeitzone gelesen und geschrieben: ein ISO-Text ohne „Z“ ist
// Ortszeit, und so bleibt 10:30 auf Server und Telefon 10:30.
//
// Seit „Mein Bereich“ echte Termine zeigt (24.10.2026), ist „heute“
// wirklich heute. `STICHTAG` bleibt der Stichtag der Musterdaten.
// ============================================================

export const STICHTAG = "2026-10-23T12:00:00";

export function jetzt(): Date {
  return new Date();
}

const TAG = 86_400_000;
const WOCHENTAG = new Intl.DateTimeFormat("de-DE", { weekday: "short" });
const WOCHENTAG_LANG = new Intl.DateTimeFormat("de-DE", { weekday: "long" });
const TAG_MONAT = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit" });
const UHR = new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" });
const MONAT_JAHR = new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" });
const LANG = new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "long" });
const MONAT_KURZ = new Intl.DateTimeFormat("de-DE", { month: "short" });

function d(iso: string): Date {
  return new Date(iso);
}

function mitternacht(x: Date): number {
  return new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
}

/** „Sa“ — ohne den Punkt, den Intl anhaengt. */
export function wochentagKurz(iso: string): string {
  return WOCHENTAG.format(d(iso)).replace(".", "");
}

/** „Sa 24.10.“ */
export function tagKurz(iso: string): string {
  return `${wochentagKurz(iso)} ${TAG_MONAT.format(d(iso))}.`;
}

/** „Samstag, 24. Oktober“ */
export function tagLang(iso: string): string {
  return `${WOCHENTAG_LANG.format(d(iso))}, ${LANG.format(d(iso))}`;
}

export function uhrzeit(iso: string): string {
  return UHR.format(d(iso));
}

/** „10:30–12:00“ oder „10:30“. */
export function zeitspanne(start: string, ende: string | null): string {
  return ende ? `${uhrzeit(start)}–${uhrzeit(ende)}` : uhrzeit(start);
}

/** Tag im Monat und Monat kurz — fuer das Datumsblatt: { tag: "24", monat: "Okt" }. */
export function datumsblatt(iso: string): { tag: string; monat: string; wochentag: string } {
  const x = d(iso);
  return { tag: String(x.getDate()), monat: MONAT_KURZ.format(x).replace(".", ""), wochentag: wochentagKurz(iso) };
}

export function monatJahr(iso: string): string {
  return MONAT_JAHR.format(d(iso));
}

/** „Heute“, „Morgen“, „Übermorgen“ — sonst `null`. */
export function relativTag(iso: string, heute: Date = jetzt()): string | null {
  const tage = Math.round((mitternacht(d(iso)) - mitternacht(heute)) / TAG);
  if (tage === 0) return "Heute";
  if (tage === 1) return "Morgen";
  if (tage === 2) return "Übermorgen";
  return null;
}

/** „in 3 Tagen“, „in 2 Wochen“ — fuer Fristen. */
export function inZeit(iso: string, heute: Date = jetzt()): string {
  const ms = d(iso).getTime() - heute.getTime();
  if (ms < 0) return "abgelaufen";
  const stunden = Math.round(ms / 3_600_000);
  if (stunden < 24) return stunden <= 1 ? "in einer Stunde" : `in ${stunden} Stunden`;
  const tage = Math.round(ms / TAG);
  if (tage < 14) return tage === 1 ? "morgen" : `in ${tage} Tagen`;
  return `in ${Math.round(tage / 7)} Wochen`;
}

/** „gerade eben“, „vor 3 Stunden“, „gestern“, „vor 4 Tagen“, sonst das Datum. */
export function vorZeit(iso: string, heute: Date = jetzt()): string {
  const ms = heute.getTime() - d(iso).getTime();
  const minuten = Math.round(ms / 60_000);
  if (minuten < 2) return "gerade eben";
  if (minuten < 60) return `vor ${minuten} Minuten`;
  const tage = Math.round((mitternacht(heute) - mitternacht(d(iso))) / TAG);
  if (tage === 0) return `vor ${Math.round(minuten / 60)} Stunden`;
  if (tage === 1) return "gestern";
  if (tage < 7) return `vor ${tage} Tagen`;
  return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }).format(d(iso));
}

export function istVorbei(iso: string, heute: Date = jetzt()): boolean {
  return d(iso).getTime() < heute.getTime();
}

export function grussZurTageszeit(heute: Date = jetzt()): string {
  const h = heute.getHours();
  if (h < 11) return "Guten Morgen";
  if (h < 18) return "Hallo";
  return "Guten Abend";
}
